import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AppLogo from "./AppLogo";
import { useAuth } from '../context/AuthContext';
import NotificationsDropdown from './NotificationsDropdown';
import { useBreakpoint } from "../hooks/useBreakpoint";
import MessagesDropdown from './MessagesDropdown';

/* ── Fonts: DM Sans replaces Inter as primary UI font ── */
if (typeof document !== "undefined" && !document.getElementById("__hero_fonts")) {
  const l = document.createElement("link");
  l.id = "__hero_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

const HERO_SERIF = "'Instrument Serif', Georgia, serif";
const F = "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif";

/* ── Brand tokens ── */
const C = {
  green:      "#1e9254",
  greenDark:  "#155c36",
  greenLight: "#2ab76b",
  greenTint:  "#eef8f3",
  greenBorder:"#a7d9bc",
  text:       "#0f1117",
  textMid:    "#2d3549",
  sub:        "#4a5268",
  faint:      "#8a93a8",
  ghost:      "#b0b8cc",
  border:     "#E5E7EB",
  white:      "#FFFFFF",
  /* Mobile hero — dark green gradient */
  greenDeep1: "#1e4a2e",
  greenDeep2: "#163322",
  greenDeep3: "#0d2e1a",
  /* Keep charcoal tokens for potential other uses */
  charcoal1:  "#23262b",
  charcoal2:  "#3a3e44",
  charcoal3:  "#1a1c1f",
};

const CITIES = [
  "Nairobi", "Mombasa", "Kisumu", "Nakuru", "Eldoret", "Thika", "Nyeri",
  "Machakos", "Kakamega", "Kisii", "Meru", "Nyahururu", "Kericho", "Embu",
  "Naivasha", "Kitale", "Malindi", "Ruiru", "Kiambu", "Kajiado", "Voi",
  "Garissa", "Lamu", "Bungoma",
];

const CITY_AREAS = {
  Nairobi: ["Westlands", "Kilimani", "Karen", "Lavington", "Kasarani", "Embakasi", "South B", "South C", "Ngong Road", "Parklands"],
  Mombasa: ["Nyali", "Bamburi", "Tudor", "Kizingo", "Likoni", "Mtwapa", "Diani", "Shanzu"],
};

function LocationDropdown({ selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [pendingCity, setPendingCity] = useState(null);
  const [dropPos, setDropPos] = useState({ top: "auto", left: "auto" });
  const wrapperRef = useRef(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setDropPos({ top: rect.bottom + 6, left: rect.left });
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      const insideWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!insideWrapper && !insideDropdown) {
        setOpen(false);
        setPendingCity(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on scroll
  useEffect(() => {
    if (!open) return;
    const handleScroll = () => { setOpen(false); setPendingCity(null); };
    window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", handleScroll, { capture: true });
  }, [open]);

  const handleSelect = (city) => { 
    onSelect(city); 
    setOpen(false); 
    setPendingCity(null);
  };

  const handleAreaSelect = (area, city) => {
    onSelect(`${area}, ${city}`);
    setOpen(false);
    setPendingCity(null);
  };

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropPos.top + "px",
            left: dropPos.left + "px",
            width: 192,
            maxHeight: 320,
            overflowY: "auto",
            background: "#fff",
            borderRadius: 14,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.06)",
            border: "1px solid #E5E7EB",
            padding: 8,
            zIndex: 99999,
            animation: "fadeSlideDown 0.15s ease both",
          }}
        >
          {pendingCity ? (
            <>
              <div
                onClick={() => setPendingCity(null)}
                style={{
                  fontSize: 13.5, padding: "9px 12px", cursor: "pointer",
                  borderRadius: 8, color: "#6B7280", fontFamily: F,
                  display: "flex", alignItems: "center", gap: 8,
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                Back to cities
              </div>
              <div style={{ height: 1, background: "#F3F4F6", margin: "4px 0 8px" }} />
              {CITY_AREAS[pendingCity]?.map((area) => (
                <div
                  key={area}
                  onClick={() => handleAreaSelect(area, pendingCity)}
                  style={{
                    fontSize: 13.5, padding: "9px 12px", cursor: "pointer",
                    borderRadius: 8, transition: "all 0.15s ease",
                    color: "#374151", fontFamily: F,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  {area}
                </div>
              ))}
            </>
          ) : (
            CITIES.map((city) => (
              <div
                key={city}
                onClick={() => {
                  if (CITY_AREAS[city]) {
                    setPendingCity(city);
                  } else {
                    handleSelect(city);
                  }
                }}
                style={{
                  fontSize: 13.5, padding: "9px 12px", cursor: "pointer",
                  borderRadius: 8, transition: "all 0.15s ease",
                  color: city === selected ? C.green : "#374151",
                  fontWeight: city === selected ? 600 : 400,
                  background: city === selected ? C.greenTint : "transparent",
                  fontFamily: F,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
                onMouseEnter={e => { if (city !== selected) e.currentTarget.style.background = "#F9FAFB"; }}
                onMouseLeave={e => { if (city !== selected) e.currentTarget.style.background = "transparent"; }}
              >
                {city}
                {CITY_AREAS[city] && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                )}
              </div>
            ))
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={wrapperRef} className="relative flex items-center px-3">
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 4,
          color: "#4B5563", fontSize: 13, fontWeight: 600,
          whiteSpace: "nowrap", background: "none", border: "none", cursor: "pointer",
          fontFamily: F, transition: "color 0.2s ease",
        }}
        onMouseEnter={e => e.currentTarget.style.color = C.text}
        onMouseLeave={e => e.currentTarget.style.color = "#4B5563"}
      >
        {selected}
        <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke="#9CA3AF" strokeWidth="2.5" style={{ flexShrink: 0 }}>
          <path d="M5 8l5 5 5-5"/>
        </svg>
      </button>
      {dropdown}
    </div>
  );
}


/* ── M-Pesa SVG mark ── */
function MPesaMark({ height = 14 }) {
  return (
    <svg
      viewBox="0 0 512 273"
      style={{ height, width: height * (512 / 273), display: "inline-block", verticalAlign: "-2px" }}
      aria-label="M-Pesa"
    >
      <path d="m361 184c-6.68-1.88-14.8-5.54-19.9-8.94-1.52-1.02-1.18-2.66 1.73-8.42l3.62-7.15 9.09 4.74c15.9 8.33 29 7.71 29-1.4 0-4.39-3.64-6.71-16.4-10.4-17.5-5.12-25.4-13.2-24.1-24.4 1.71-14 12.7-21.4 31.2-21.3 10.9 0.114 26.2 4.19 28.2 7.54 0.754 1.22-4.68 15.5-5.89 15.5-0.323 0-2.95-1.18-5.82-2.62-13.7-6.88-27.1-6.75-27.1 0.259 0 4.67 3.4 7.15 14.8 10.8 14.8 4.79 20 8.11 23.4 15.1 3.36 6.94 2.86 14-1.48 21.2-5.65 9.29-25.2 13.9-40.6 9.62zm-351-38.1v-38.8h21.2l11.3 22.6c6.22 12.4 11.6 22.6 12 22.6s5.77-10.2 12-22.6l11.3-22.6h21.2v77.6h-16.9v-24c0-13.2-0.486-24-1.08-24-0.595 0-5.3 8.88-10.5 19.7l-9.38 19.7h-13.1l-20.3-41.6-0.77 50.1h-16.9zm188-0.203v-38.9l21.7 0.73c18.6 0.626 22.7 1.18 28.2 3.85 9.45 4.57 12.7 9.93 13.3 21.9 0.942 19.9-8.5 28.6-32.1 30l-12.7 0.725v20.7h-18.3zm41.7-0.948c3.02-2.59 3.79-4.5 3.79-9.29 0-9.26-3.54-11.9-16.6-12.6l-10.6-0.533v25.7h9.79c8.21 0 10.4-0.53 13.6-3.27zm31.6 1.15v-38.8h59.2v15.5h-40.9v15.5h38.1v15.5h-38.1v15.5h42.3v15.5h-60.6zm142 30.7c1.93-4.5 9.38-21.9 16.5-38.5l13-30.3 19.7-0.805 15.1 36.4c8.32 20 15.5 37.5 16.1 38.9 0.83 2.31 0.0141 2.5-9.31 2.12l-10.2-0.417-4.68-12.7h-34.8l-4.95 12.7-20 0.828zm49.6-21.9c-6.36-17.3-10.1-26.4-10.8-26.4-0.478 7e-3 -3.09 5.84-5.81 13-2.71 7.12-5.15 13.5-5.4 14.1-0.258 0.625 4.72 1.14 11.1 1.14 8.64 0 11.4-0.443 10.9-1.76z" fill="#39b54a" stroke="#39b54a" strokeWidth="2"/>
      <path d="m172 80.6v9.36c-15.2 0.0524-30.2 0.0158-45.3 0.0158-12.9 0-14.4 6.09-14.4 15.7v131c0 7.81 6.44 14.1 14.4 14.1h43.1c7.99 0 14.4-6.29 14.4-14.1v-157c0-1.97-1.59-5.95-5.39-5.77-4.27 0.237-6.78 3.37-6.91 5.77zm-37.1 27.1h27.6c11.6 0 13.2 7.25 13.2 15.1v46.7c0 5.8-5.03 14.9-13.2 14.9h-27.6c-9.97 0-15.2-8.6-15.2-14.9v-46.7c0-7.81 4.13-15.1 15.2-15.1z" fill="#d8e3d2" fillRule="evenodd" strokeWidth=".998"/>
      <path d="m104 155c9.07-0.942 17.2-5.6 26.4-17.6 10.4 15.8 24.7 15.2 39.2 16.8-15.6 0.163-13.6 5.52-34.8 4.03-6.77-0.473-17.9-0.936-30.7-3.19z" fill="#9d4c44" fillRule="evenodd" strokeWidth=".998"/>
      <path d="m161 122 33.8 16.4c-24.1 26.9-58.5 25.1-90.2 16.8 17.5-0.0728 33 6.4 56.3-33.1z" fill="#ed1c24" fillRule="evenodd" strokeWidth=".998"/>
    </svg>
  );
}

/* ── Cart icon — solid glyph matching the reference: filled cart body,
       cut-out grid lines, and two wheel dots ── */
function CartIcon({ count = 0, onClick, size = 36 }) {
  const [hovered, setHovered] = useState(false);
  const hasItems = count > 0;
  const badgeBg = hovered ? "#CDEEDC" : "#DFF5E7";
  const iconSize = Math.round(size * 0.528);
  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        onClick={onClick} title="Cart"
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        style={{
          width: size, height: size, borderRadius: "50%",
          background: badgeBg,
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: hovered ? "0 3px 10px rgba(21,92,54,0.28)" : "0 2px 6px rgba(21,92,54,0.18)",
          transition: "background 150ms ease, box-shadow 150ms ease, transform 150ms ease",
          transform: hovered ? "translateY(-1px)" : "translateY(0)",
          padding: 0, position: "relative", flexShrink: 0,
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24">
          {/* solid cart body */}
          <path
            d="M4 5h2.2c.5 0 .93.34 1.05.82L7.7 8H20a1 1 0 0 1 .97 1.24l-1.8 7.2A2 2 0 0 1 17.24 18H9.5a2 2 0 0 1-1.94-1.51L5.02 6.3A.3.3 0 0 0 4.73 6H4a1 1 0 1 1 0-2z"
            fill={C.greenDark}
          />
          {/* basket grid lines — drawn in the badge background color so they
              read as cut-outs of the solid fill, matching the reference icon */}
          <line x1="10.3" y1="9.5" x2="10.3" y2="16.4" stroke={badgeBg} strokeWidth="1.1" />
          <line x1="13.6" y1="9.5" x2="13.6" y2="16.4" stroke={badgeBg} strokeWidth="1.1" />
          <line x1="16.9" y1="9.5" x2="16.9" y2="16.4" stroke={badgeBg} strokeWidth="1.1" />
          <line x1="7.9" y1="12.6" x2="19.6" y2="12.6" stroke={badgeBg} strokeWidth="1.1" />
          {/* wheels */}
          <circle cx="10" cy="20" r="1.5" fill={C.greenDark} />
          <circle cx="17" cy="20" r="1.5" fill={C.greenDark} />
        </svg>
      </button>
      {hasItems && (
        <span style={{
          position: "absolute", top: -3, right: -3,
          minWidth: 17, height: 17, borderRadius: 9, padding: "0 4px",
          background: C.green, border: "2px solid #fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, fontWeight: 700, color: "#fff", fontFamily: F,
          pointerEvents: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
        }}>{Math.min(count, 9)}</span>
      )}
    </div>
  );
}

/* ── Pro Showcase Strip ── */
const PRO_SHOWCASE = [
  { id: 201, name: "Wanjiru M.", tier: "Verified Pro", category: "Cleaning", slug: "cleaning", location: "Kilimani, Nairobi", rating: 4.9, jobs: 41, photo: "/images/jiraniPros/Pro3.jpg" },
  { id: 202, name: "Brian O.", tier: "Verified Pro", category: "Errands", slug: "errands-delivery", location: "Nyali, Mombasa", rating: 4.8, jobs: 27, photo: "/images/jiraniPros/Pro7.jpg" },
  { id: 203, name: "Kevin M.", tier: "Verified Pro", category: "Bodaboda courier", slug: "bodaboda-couriers", location: "Kondele, Kisumu", rating: 4.9, jobs: 58, photo: "/images/jiraniPros/Pro11.jpg" },
  { id: 204, name: "Faith N.", tier: "Verified Pro", category: "Gardening", slug: "gardening", location: "Milimani, Kisumu", rating: 4.7, jobs: 19, photo: "/images/jiraniPros/Pro5.jpg" },
  { id: 205, name: "Amina S.", tier: "Verified Pro", category: "Home cook", slug: "chef", location: "Bamburi, Mombasa", rating: 4.9, jobs: 34, photo: "/images/jiraniPros/Pro14.jpg" },
  { id: 206, name: "Dennis K.", tier: "Verified Pro", category: "Food courier", slug: "errands-delivery", location: "Karen, Nairobi", rating: 4.8, jobs: 22, photo: "/images/jiraniPros/Pro9.jpg" },
  { id: 207, name: "Njeri W.", tier: "Verified Pro", category: "Electrical", slug: "electrical", location: "Naivasha Rd, Nakuru", rating: 4.9, jobs: 49, photo: "/images/jiraniPros/Pro5.jpg" },
];

function ShowcaseTierBadge() {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: "rgba(238,248,243,0.95)",
      border: "1px solid #a7d9bc",
      color: "#155c36",
      fontSize: 8.5, fontWeight: 700, padding: "3px 7px",
      borderRadius: 6, letterSpacing: ".02em", fontFamily: F,
    }}>
      <svg width="9" height="9" viewBox="0 0 24 24" fill="#1e9254" stroke="none">
        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
        <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      Verified Pro
    </div>
  );
}

function ProShowcaseStrip({ isMobile, navigate }) {
  const cardHeight = isMobile ? 178 : 196;
  const cardWidth = isMobile ? 132 : 148;

  const content = (
    <>
      <p style={{
        color: "rgba(255,255,255,0.85)",
        fontSize: isMobile ? 12 : 15,
        fontWeight: 400,
        fontStyle: "normal",
        letterSpacing: isMobile ? "0.02em" : "0.03em",
        textTransform: "none",
        marginBottom: isMobile ? 10 : 12,
        fontFamily: HERO_SERIF,
      }}>
        On <em style={{ fontStyle: "italic", color: "#7CDB9F" }}>JiraniApp</em> Right Now
      </p>
      <div style={{
        display: "flex",
        gap: isMobile ? 14 : 10,
        overflowX: "auto",
        overflowY: "visible",
        paddingBottom: 4,
        paddingRight: 24,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        WebkitOverflowScrolling: "touch",
        maskImage: "linear-gradient(to right, #000 0%, #000 calc(100% - 28px), transparent 100%)",
        WebkitMaskImage: "linear-gradient(to right, #000 0%, #000 calc(100% - 28px), transparent 100%)",
      }}
      className="pro-scroll-strip"
      >
        <style>{`.pro-scroll-strip::-webkit-scrollbar { display: none; }`}</style>
        {PRO_SHOWCASE.map((pro, idx) => {
          const handleClick = () => {
            navigate(`/pro/${pro.id}`);
          };
          return (
            <div
              key={idx}
              onClick={handleClick}
              style={{
                flex: "0 0 auto",
                width: cardWidth,
                height: cardHeight,
                borderRadius: 12,
                overflow: "hidden",
                cursor: "pointer",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                position: "relative",
                background: "rgba(0,0,0,0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.30)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div style={{ position: "relative", width: "100%", height: "100%" }}>
                <img
                  src={pro.photo}
                  alt={pro.name}
                  loading="lazy"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                />
                <div style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 45%, transparent 68%)",
                }} />
                <div style={{ position: "absolute", top: 8, left: 8 }}>
                  <ShowcaseTierBadge />
                </div>
                <div style={{ position: "absolute", bottom: 8, left: 10, right: 10 }}>
                  <p style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: "#fff", margin: "0 0 1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pro.name}</p>
                  <p style={{ fontSize: isMobile ? 10 : 11, color: "rgba(255,255,255,0.75)", margin: "0 0 3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pro.category} · {pro.location}</p>
                  <p style={{ fontSize: isMobile ? 10 : 11, color: "#fff", margin: 0, display: "flex", alignItems: "center", gap: 3 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="#FBBF24" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    {pro.rating} · {pro.jobs} jobs
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  if (isMobile) {
    return <div style={{ width: "100%" }}>{content}</div>;
  }

  return (
    <div style={{
      background: "rgba(13,46,26,0.55)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      borderRadius: 16,
      padding: "16px 18px",
      display: "block",
      width: "100%",
      boxSizing: "border-box",
    }}>
      {content}
    </div>
  );
}

const SEARCH_SUGGESTIONS = [
  "Cleaning & Laundry", "Carpet Cleaning", "Deep House Cleaning",
  "Office Cleaning", "Laundry & Dry Cleaning", "Airbnb Cleaning",
  "Plumbing Services", "Pipe Repair", "Drain Unblocking",
  "Water Heater Installation", "Borehole Services",
  "Electrical Services", "Electrical Installation", "Solar Panel Setup", "Generator Repair",
  "Electrical Wiring", "AC Installation & Repair",
  "TV & Electronics Repair", "TV Repair", "Appliance Repair", "Phone Repair", "AC Repair",
  "Hair & Beauty", "Hair Braiding", "Hair Coloring", "Nails & Manicure",
  "Makeup Artist", "Barber Services",
  "Boda Boda Rides", "Boda Boda Delivery", "Boda Boda Transport",
  "Courier Delivery", "Moving & Relocation", "Furniture Delivery",
  "Photography", "Event Photography", "Portrait Photography", "Videography",
  "Home Tutoring", "KCSE Exam Preparation", "Mathematics Tutoring",
  "Babysitting & Childcare", "Babysitting", "Nanny Services", "After-School Care",
  "Fitness & Training", "Personal Trainer", "Home Workouts", "Yoga Instructor",
  "Nutrition Coaching", "Massage Therapy",
  "Private Chef", "Catering Services", "Meal Prep",
  "Carpentry & Furniture", "Carpentry", "Custom Furniture", "Cabinet Installation",
  "Furniture Assembly", "Furniture Repair", "Interior Painting",
  "Gardening & Landscaping", "Lawn Mowing", "Dog Grooming",
  "Auto Repair", "Engine Diagnostics", "Brake Repair", "Car Servicing", "Oil Change",
  "Errands & Delivery", "Grocery Shopping", "Package Delivery", "Queue Standing",
];

const POPULAR_SUGGESTIONS = [
  { label: "Cleaning", icon: "🧹" },
  { label: "Plumbing", icon: "🔧" },
  { label: "Electrical", icon: "⚡" },
  { label: "Tutoring", icon: "📚" },
  { label: "Beauty", icon: "💇" },
  { label: "Auto Repair", icon: "🚗" },
  { label: "Moving", icon: "📦" },
  { label: "Fitness", icon: "🏋️" },
];

const NEAR_YOU_SUGGESTIONS = [
  { label: "Services near you", icon: "📍" },
  { label: "Available now", icon: "🟢" },
];

const categories = [
  { name: "Cleaning & Laundry", slug: "cleaning", columns: [{ heading: "Popular Services", links: ["Mama Fua (Laundry)", "Carpet & Upholstery", "Home Deep Clean", "Office Cleaning"] }] },
  { name: "Plumbing Services", slug: "plumbing", columns: [{ heading: "Popular Services", links: ["Pipe Repairs", "Drain Unblocking", "Tap Installation", "Water Tank Fixing"] }] },
  { name: "TV & Electronics Repair", slug: "electronics-repair", columns: [{ heading: "Popular Services", links: ["TV Screen Repair", "Home Theatre Setup", "Decoder Installation", "Appliance Repair"] }] },
  { name: "Hair & Beauty", slug: "hair-beauty", columns: [{ heading: "Popular Services", links: ["Hair Braiding", "Nails & Manicure", "Makeup Artist", "Lashes & Brows"] }] },
  { name: "Home Tutoring", slug: "home-tutoring", columns: [{ heading: "Popular Services", links: ["Primary Tutoring", "High School Subjects", "University Help", "KCSE Prep"] }] },
  { name: "Photography", slug: "photography", columns: [{ heading: "Popular Services", links: ["Event Photography", "Portrait Sessions", "Product Shoots", "Videography"] }] },
  { name: "Electrical Services", slug: "electrical", columns: [{ heading: "Popular Services", links: ["Wiring & Rewiring", "Solar Installation", "Lighting Setup", "Generator Repair"] }] },
  { name: "Gardening & Landscaping", slug: "gardening", columns: [{ heading: "Popular Services", links: ["Lawn Mowing", "Garden Design", "Tree Trimming", "Irrigation Setup"] }] },
  { name: "Carpentry & Furniture", slug: "carpentry-furniture", columns: [{ heading: "Popular Services", links: ["Custom Furniture", "Cabinet Installation", "Furniture Repair", "Wood Finishing"] }] },
  { name: "Moving & Relocation", slug: "moving", columns: [{ heading: "Popular Services", links: ["House Moving", "Office Relocation", "Packing Services", "Furniture Transport"] }] },
  { name: "Babysitting & Childcare", slug: "childcare", columns: [{ heading: "Popular Services", links: ["Babysitting", "Nanny Services", "After-School Care", "Newborn Care"] }] },
  { name: "Fitness & Training", slug: "fitness", columns: [{ heading: "Popular Services", links: ["Personal Training", "Home Workouts", "Yoga Sessions", "Nutrition Coaching"] }] },
  { name: "Auto Repair", slug: "auto-repair", columns: [{ heading: "Popular Services", links: ["Engine Diagnostics", "Brake Repair", "Oil Change", "Car Servicing"] }] },
  { name: "Errands & Delivery", slug: "errands-delivery", columns: [{ heading: "Popular Services", links: ["Grocery Shopping", "Package Delivery", "Pickup & Drop-off", "Queue Standing"] }] },
  { name: "Personal Chef", slug: "chef", columns: [{ heading: "Popular Services", links: ["Private Chef", "Catering Services", "Meal Prep", "Cooking Classes"] }] },
];

const EXPLORE_LINKS = [
  { label: "Find a JiraniPro",       path: "/find-a-jiranipro" },
  { label: "How JiraniApp Works",    path: "/how-it-works" },
  { label: "JiraniApp for Business", path: "/business" },
  { label: "Browse by Location",     path: "/browse-by-location" },
  { label: "Customer Reviews",       path: "/customer-reviews" },
  { label: "JiraniApp Guarantee",    path: "/guarantee" },
  { label: "About JiraniApp",        path: "/about" },
  { label: "Property Management",    path: "/property-management" },
  { label: "Corporate Services",     path: "/corporate" },
  { label: "Trust & Safety",         path: "/trust-and-safety" },
  { label: "Community Hub",          path: "/community-hub" },
  { label: "Tips & Resources",       path: "/tips" },
  { label: "Events",                 path: "/events" },
  { label: "Careers",                path: "/careers" },
  { label: "Contact Sales",          path: "/contact-sales" },
];

function MessagesIcon({ unreadCount = 0, onClick }) {
  const count = Math.min(unreadCount, 99);
  const hasUnread = count > 0;
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        onClick={onClick} title="Messages"
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        style={{
          width: 34, height: 34, borderRadius: 10,
          background: hovered ? "#F3F4F6" : "transparent",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#6B7280", transition: "background 150ms ease", padding: 0, position: "relative",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </button>
      {hasUnread && (
        <span style={{ position: "absolute", top: 5, right: 5, width: 7, height: 7, borderRadius: "50%", background: C.green, border: "1.5px solid #F3F4F6", pointerEvents: "none" }}/>
      )}
    </div>
  );
}

/* ── Profile Dropdown ── */
function ProfileDropdown({ user, onLogout, navigate, scrolled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const initials = user?.name ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() : "JN";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: 2, borderRadius: 999, boxShadow: `0 0 0 2px ${open ? C.green : scrolled ? C.greenBorder : "rgba(255,255,255,0.7)"}`, transition: "box-shadow 0.2s ease" }}>
        <div style={{ position: "relative" }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: scrolled ? `linear-gradient(145deg, ${C.green}22, ${C.greenTint})` : "#fff",
            border: scrolled ? "2px solid #fff" : "2px solid rgba(255,255,255,0.9)",
            boxShadow: scrolled ? "0 1px 4px rgba(30,146,84,0.18)" : "0 2px 8px rgba(0,0,0,0.22)",
            overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.greenDark, fontFamily: F }}>{initials}</span>
          </div>
          <span style={{ position: "absolute", bottom: -1, right: -1, width: 9, height: 9, borderRadius: "50%", background: C.green, border: "2px solid #fff" }}/>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={scrolled ? "#8b93a7" : "rgba(255,255,255,0.85)"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 10px)", right: 0, zIndex: 9999,
          background: "#FFFFFF", border: "1px solid #F0F0F0", borderRadius: 12, padding: 8, minWidth: 180,
          boxShadow: "0 8px 24px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)",
          animation: "fadeSlideDown 0.18s ease both",
        }}>
          <div style={{ padding: "8px 10px 10px", borderBottom: "1px solid #F3F4F6", marginBottom: 4 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: F }}>{user?.name || "My Account"}</p>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9CA3AF", fontFamily: F }}>{user?.email || ""}</p>
          </div>

          {[
            { label: "Profile", path: "/account", icon: (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>) },
            { label: "Help & Support", path: "/help", icon: (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>) },
          ].map((item) => (
            <div key={item.label} onClick={() => { setOpen(false); navigate(item.path); }}
              style={{ padding: "8px 10px", borderRadius: 6, fontSize: 13, color: "#333", fontFamily: F, cursor: "pointer", transition: "background 0.15s ease", display: "flex", alignItems: "center", gap: 8 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#F7F7F7")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span style={{ color: "#6B7280", display: "flex" }}>{item.icon}</span>
              {item.label}
            </div>
          ))}

          <div style={{ borderTop: "1px solid #F3F4F6", marginTop: 4, paddingTop: 4 }}>
            <div onClick={() => { setOpen(false); onLogout?.(); }}
              style={{ padding: "8px 10px", borderRadius: 6, fontSize: 13, fontWeight: 600, color: "#D92D20", fontFamily: F, cursor: "pointer", transition: "background 0.15s ease", display: "flex", alignItems: "center", gap: 8 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#FEF2F2")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Log out
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Nav link button — premium sizing, darker resting color ── */
function getNavLinkStyle(scrolled) {
  return {
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontWeight: 500,
    letterSpacing: "-0.1px",
    background: "none",
    border: "none",
    fontSize: 13.5,
    color: scrolled ? "#4a5268" : "rgba(255,255,255,0.88)",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "11px 14px",
    borderRadius: 11,
    minHeight: 44,
    transition: "background 0.16s ease, color 0.16s ease",
  };
}

function getHeroCta(isLoggedIn, role) {
  if (!isLoggedIn) return { label: "Register", path: "/signup" };
  if (role === "provider") return { label: "Switch to Pro Mode", path: "/pro/dashboard" };
  return { label: "Earn as a JiraniPro", path: "/pro-signup" };
}
function NavLinks({ authState, openAuth, navigate, openBrowse, setOpenBrowse, navLinkStyle, dropdownStyle, exploreLinks, scrolled, cartCount, onCartClick }) {
  const { isLoggedIn, role, user, onLogout } = authState;

  const ExploreDropdown = (
    <div style={{ position: "relative" }}>
      <button
        className="nav-text-link"
        onClick={(e) => { e.stopPropagation(); setOpenBrowse((prev) => !prev); }}
        style={navLinkStyle}
      >
        Explore
        <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" style={{ transform: openBrowse ? "rotate(180deg)" : "rotate(0)", transition: "transform .18s" }}>
          <path d="M5 8l5 5 5-5"/>
        </svg>
      </button>
      {openBrowse && (
        <div style={{ ...dropdownStyle, width: 280, maxHeight: 440, overflowY: "auto" }}>
          {exploreLinks.map((link) => (
            <div key={link.path}
              style={{ padding: "9px 12px", borderRadius: 10, cursor: "pointer", fontSize: 14, color: "#374151", fontFamily: F, transition: "all 0.2s ease" }}
              onClick={() => { setOpenBrowse(false); navigate(link.path); }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {link.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );

const ctaStyle = {
  background: C.green, color: "#fff", border: "none", borderRadius: 999,
  padding: "12px 26px", fontWeight: 400, cursor: "pointer", whiteSpace: "nowrap",
  fontFamily: HERO_SERIF, fontSize: 16, flexShrink: 0,
  boxShadow: "0 4px 16px rgba(30,146,84,0.28)",
  letterSpacing: 0, transition: "all 0.2s ease",
  marginLeft: 6,
};

  const heroCta = getHeroCta(isLoggedIn, role);
  const HeroCtaButton = (
    <button className="cta-btn" onClick={() => navigate(heroCta.path)} style={ctaStyle}>
      {heroCta.label}
    </button>
  );

  if (!isLoggedIn) {
    return (
      <>
        {ExploreDropdown}
        <button className="nav-text-link" onClick={() => navigate(buildSearchUrlSafe())} style={navLinkStyle}>Find Help</button>
        <button className="nav-text-link" onClick={() => navigate("/become-a-pro")} style={navLinkStyle}>Offer Services</button>
        <button className="nav-text-link" onClick={() => openAuth && openAuth("login")} style={navLinkStyle}>Log in</button>
        {HeroCtaButton}
      </>
    );
  }

  if (role === "provider") {
    return (
      <>
        {ExploreDropdown}
        <button className="nav-text-link" onClick={() => navigate("/jobs")} style={navLinkStyle}>Jobs</button>
        <button className="nav-text-link" onClick={() => navigate("/my-services")} style={navLinkStyle}>My Services</button>
        {HeroCtaButton}
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CartIcon count={cartCount} onClick={onCartClick} />
          <NotificationsDropdown />
          <MessagesDropdown />
        </div>
        <ProfileDropdown user={user} onLogout={onLogout} navigate={navigate} scrolled={scrolled} />
      </>
    );
  }

  return (
    <>
      {ExploreDropdown}
      <button className="nav-text-link" onClick={() => navigate(buildSearchUrlSafe())} style={navLinkStyle}>Find Help</button>
      <button className="nav-text-link" onClick={() => navigate("/bookings")} style={navLinkStyle}>My Bookings</button>
      {HeroCtaButton}
      <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CartIcon count={cartCount} onClick={onCartClick} />
        <NotificationsDropdown />
        <MessagesDropdown />
      </div>
      <ProfileDropdown user={user} onLogout={onLogout} navigate={navigate} scrolled={scrolled} />
    </>
  );
}

function buildSearchUrlSafe() {
  try {
    const loc = localStorage.getItem("jirani_location");
    return loc ? `/search?location=${encodeURIComponent(loc)}` : "/search";
  } catch { return "/search"; }
}

function SearchFocusDropdown({ onSelect }) {
  return (
    <div style={{
      position: "absolute", top: "calc(100% + 8px)", left: 0, width: "100%",
      background: "#fff", borderRadius: 14, boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
      zIndex: 200, overflow: "hidden", animation: "fadeSlideDown 0.15s ease both", padding: "12px 0",
    }}>
      <p style={{ margin: "0 0 6px", padding: "0 20px", fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: F }}>Popular</p>
      {POPULAR_SUGGESTIONS.map((s, i) => (
        <div key={i} onMouseDown={() => onSelect(s.label)}
          style={{ padding: "9px 20px", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, color: "#374151", fontFamily: F, transition: "background 0.15s ease" }}
          onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
          {s.label}
        </div>
      ))}
      <div style={{ margin: "10px 20px", height: 1, background: "#F3F4F6" }} />
      <p style={{ margin: "0 0 6px", padding: "0 20px", fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: F }}>Near You</p>
      {NEAR_YOU_SUGGESTIONS.map((s, i) => (
        <div key={i} onMouseDown={() => onSelect(s.label)}
          style={{ padding: "9px 20px", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, color: "#374151", fontFamily: F, transition: "background 0.15s ease" }}
          onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
          {s.label}
        </div>
      ))}
    </div>
  );
}

/* ── Mobile drawer menu — visually overhauled ── */
function MobileMenuDrawer({ open, onClose, navigate, openAuth, authState, buildSearchUrl, exploreLinks, scrolled, cartCount, onCartClick }) {
  const [exploreOpen, setExploreOpen] = useState(false);
  if (!open) return null;

  const { isLoggedIn, role, user, onLogout } = authState;
  const handleNav = (path) => { onClose(); navigate(path); };
  const handleAuth = (mode) => { onClose(); openAuth && openAuth(mode); };

  const initials = user?.name ? user.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "JN";

  /* Icon set for drawer items */
  const icons = {
    explore:  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
    findHelp: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
    offer:    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41 13.41 20.59a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
    jobs:     <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
    services: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5Z"/><path d="m2 17 10 5 10-5"/><path d="m2 12 10 5 10-5"/></svg>,
    bookings: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>,
    messages: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    profile:  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    help:     <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    cart:     <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4h2l.4 2M5.4 6h15l-1.6 8.4a2 2 0 0 1-2 1.6H8.2a2 2 0 0 1-2-1.6L5.4 6z"/><path d="M9 10v4M12 10v4M15 10v4"/><circle cx="9" cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/></svg>,
  };

  const Row = ({ icon, label, onClick, badge }) => {
    const [h, setH] = useState(false);
    return (
      <button
        onClick={onClick}
        onTouchStart={() => setH(true)} onTouchEnd={() => setH(false)}
        style={{
          display: "flex", alignItems: "center", gap: 13,
          width: "100%", padding: "14px 4px",
          background: h ? "rgba(255,255,255,0.08)" : "transparent",
          border: "none", borderRadius: 12, cursor: "pointer", textAlign: "left",
          transition: "background 0.15s ease",
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: C.greenTint, border: `1px solid ${C.greenBorder}`,
          display: "flex", alignItems: "center", justifyContent: "center", color: C.green,
        }}>{icon}</div>
        <span style={{ flex: 1, fontSize: 15.5, fontWeight: 500, color: "#FFFFFF", fontFamily: F }}>{label}</span>
        {badge}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
      </button>
    );
  };

  const SectionLabel = ({ children }) => (
    <p style={{
      fontSize: 10.5, fontWeight: 700, color: "rgba(255,255,255,0.55)",
      textTransform: "uppercase", letterSpacing: ".09em", margin: "20px 4px 6px", fontFamily: F,
    }}>{children}</p>
  );

  const handleExploreLinkClick = (link) => { onClose(); navigate(link.path); };

  return (
    <div style={{
      position: "fixed", top: 60, left: 0, right: 0, bottom: 0,
      background: `radial-gradient(ellipse at 30% 15%, ${C.greenDeep1} 0%, ${C.greenDeep2} 45%, ${C.greenDeep3} 100%)`,
      backgroundAttachment: "fixed",
      zIndex: 999, overflowY: "auto",
      padding: "8px 20px 28px", display: "flex", flexDirection: "column",
      borderTop: "none",
    }}>

      {/* Explore — collapsible */}
      <SectionLabel>Browse</SectionLabel>
      <button
        onClick={() => setExploreOpen(p => !p)}
        style={{
          display: "flex", alignItems: "center", gap: 13, width: "100%",
          padding: "14px 4px", background: "transparent", border: "none",
          borderRadius: 12, cursor: "pointer", textAlign: "left",
        }}
      >
        <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: C.greenTint, border: `1px solid ${C.greenBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: C.green }}>{icons.explore}</div>
        <span style={{ flex: 1, fontSize: 15.5, fontWeight: 500, color: "#FFFFFF", fontFamily: F }}>Explore</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2.2" strokeLinecap="round"
          style={{ transform: exploreOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>
      {exploreOpen && (
        <div style={{ paddingLeft: 49, paddingBottom: 4, maxHeight: 320, overflowY: "auto" }}>
          {exploreLinks.map((link) => (
            <button key={link.path}
              onClick={() => handleExploreLinkClick(link)}
              style={{
                background: "none", border: "none", cursor: "pointer", textAlign: "left",
                padding: "9px 4px", fontSize: 14, fontWeight: 450,
                color: "rgba(255,255,255,0.7)",
                fontFamily: F, width: "100%", display: "block",
              }}
            >{link.label}</button>
          ))}
        </div>
      )}

      {!isLoggedIn && (
        <>
          <Row icon={icons.findHelp} label="Find Help" onClick={() => handleNav(buildSearchUrl({}))}/>
          <Row icon={icons.offer} label="Offer Services" onClick={() => handleNav("/pro-signup")}/>

          <SectionLabel>Account</SectionLabel>
          <Row icon={icons.profile} label="Log in" onClick={() => handleAuth("login")}/>

          <button
            onClick={() => handleNav("/signup")}
            style={{
              background: C.green, color: "#fff", border: "none", borderRadius: 14,
              padding: "15px 20px", fontSize: 15.5, fontWeight: 700, cursor: "pointer",
              fontFamily: F, marginTop: 18, width: "100%", textAlign: "center",
              boxShadow: "0 4px 16px rgba(30,146,84,0.28)",
            }}
          >Become a Jirani</button>
        </>
      )}

      {isLoggedIn && role === "provider" && (
        <>
          <Row icon={icons.jobs} label="Jobs" onClick={() => handleNav("/jobs")}/>
          <Row icon={icons.services} label="My Services" onClick={() => handleNav("/my-services")}/>

          <SectionLabel>Account</SectionLabel>
          <Row icon={icons.messages} label="Messages" onClick={() => handleNav("/messages")}/>
          <Row icon={icons.profile} label="Profile" onClick={() => handleNav("/account")}/>
          <Row icon={icons.help} label="Help & Support" onClick={() => handleNav("/help")}/>

          <button
            onClick={() => { onClose(); onLogout?.(); }}
            style={{
              marginTop: 18, padding: "13px 4px", fontSize: 14.5, fontWeight: 600,
              color: "#D92D20", background: "#FEF2F2", border: "none", borderRadius: 12,
              cursor: "pointer", fontFamily: F, textAlign: "center", width: "100%",
            }}
          >Log out</button>
        </>
      )}

      {isLoggedIn && role !== "provider" && (
        <>
          <Row icon={icons.findHelp} label="Find Help" onClick={() => handleNav(buildSearchUrl({}))}/>
          <Row icon={icons.bookings} label="My Bookings" onClick={() => handleNav("/bookings")}/>

          <SectionLabel>Account</SectionLabel>
          <Row icon={icons.messages} label="Messages" onClick={() => handleNav("/messages")}/>
          <Row icon={icons.profile} label="Profile" onClick={() => handleNav("/account")}/>
          <Row icon={icons.help} label="Help & Support" onClick={() => handleNav("/help")}/>

          <button
            onClick={() => { onClose(); onLogout?.(); }}
            style={{
              marginTop: 18, padding: "13px 4px", fontSize: 14.5, fontWeight: 600,
              color: "#D92D20", background: "#FEF2F2", border: "none", borderRadius: 12,
              cursor: "pointer", fontFamily: F, textAlign: "center", width: "100%",
            }}
          >Log out</button>
        </>
      )}
    </div>
  );
}

export default function HeroTopSection({
  onShowNavSearchChange,
  navQuery,
  setNavQuery,
  openAuth,
  authState = { isLoggedIn: false, role: null, user: null, onLogout: null },
  cartCount = 0,
  onCartClick,
}) {
  const navigate = useNavigate();
  const { isLoggedIn, user, logoutUser } = useAuth();
  const { isMobile } = useBreakpoint();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const resolvedAuthState = {
    isLoggedIn: authState.isLoggedIn || isLoggedIn,
    role: authState.role || user?.role || null,
    user: authState.user || user || null,
    onLogout: authState.onLogout || logoutUser || null,
    hasUnread: authState.hasUnread ?? false,
    unreadMessages: authState.unreadMessages ?? 0,
  };

  const [selectedLocation, setSelectedLocation] = useState(() => {
    try { return localStorage.getItem("jirani_location") || "Nairobi"; } catch { return "Nairobi"; }
  });

  const handleLocationChange = (city) => {
    setSelectedLocation(city);
    try { localStorage.setItem("jirani_location", city); } catch {}
    window.dispatchEvent(new CustomEvent("jirani:locationchange", { detail: city }));
    if (typeof window !== "undefined" && window.location.pathname === "/search") {
      const params = new URLSearchParams(window.location.search);
      params.set("location", city);
      navigate(`/search?${params.toString()}`, { replace: true });
    }
  };

  const buildSearchUrl = (extraParams = {}) => {
    const params = new URLSearchParams(extraParams);
    if (selectedLocation) params.set("location", selectedLocation);
    const qs = params.toString();
    return qs ? `/search?${qs}` : "/search";
  };

  const handleCartClick = onCartClick || (() => navigate("/cart"));

  const [heroQuery, setHeroQuery] = useState("");
  const [heroSuggestions, setHeroSuggestions] = useState([]);
  const [showHeroSuggestions, setShowHeroSuggestions] = useState(false);
  const [showHeroFocusDropdown, setShowHeroFocusDropdown] = useState(false);
  const [navSuggestions, setNavSuggestions] = useState([]);
  const [showNavSuggestions, setShowNavSuggestions] = useState(false);
  const [openBrowse, setOpenBrowse] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  /* Navbar renders green whenever unscrolled, or whenever the mobile menu is open
     (so the header always matches the green drawer background) */
  const navAppearsGreen = !scrolled || (isMobile && mobileMenuOpen);
  const [openCategory, setOpenCategory] = useState(null);
  const [showNavSearch, setShowNavSearch] = useState(false);
  const [pillsCanScrollLeft, setPillsCanScrollLeft] = useState(false);
  const [pillsCanScrollRight, setPillsCanScrollRight] = useState(true);
  const [subcatDropPos, setSubcatDropPos] = useState(null);
  /* Mobile-only: search expanded state (icon-only -> input) */
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const navRef = useRef(null);
  const categoryRef = useRef(null);
  const heroSearchRef = useRef(null);
  const heroRef = useRef(null);
  const pillsScrollRef = useRef(null);
  const mobileSearchInputRef = useRef(null);

  const updatePillsScroll = useCallback(() => {
    const el = pillsScrollRef.current;
    if (!el) return;
    setPillsCanScrollLeft(el.scrollLeft > 8);
    setPillsCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);
  const scrollPills = (dir) => {
    const el = pillsScrollRef.current; if (!el) return;
    el.scrollBy({ left: dir * 260, behavior: "smooth" });
    setTimeout(updatePillsScroll, 320);
  };
  useEffect(() => {
    const el = pillsScrollRef.current; if (!el) return;
    updatePillsScroll();
    const ro = new ResizeObserver(() => updatePillsScroll()); ro.observe(el);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(updatePillsScroll);
    window.addEventListener("resize", updatePillsScroll);
    return () => { ro.disconnect(); window.removeEventListener("resize", updatePillsScroll); };
  }, [updatePillsScroll]);


  useEffect(() => {
    if (heroQuery.trim().length < 2) { setHeroSuggestions([]); setShowHeroSuggestions(false); return; }
    const matches = SEARCH_SUGGESTIONS.filter(s => s.toLowerCase().includes(heroQuery.toLowerCase())).slice(0, 8);
    setHeroSuggestions(matches); setShowHeroSuggestions(matches.length > 0); setShowHeroFocusDropdown(false);
  }, [heroQuery]);

  useEffect(() => {
    const q = navQuery || "";
    if (q.trim().length < 2) { setNavSuggestions([]); setShowNavSuggestions(false); return; }
    const matches = SEARCH_SUGGESTIONS.filter(s => s.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
    setNavSuggestions(matches); setShowNavSuggestions(matches.length > 0);
  }, [navQuery]);

  const doSearch = (q) => {
    if (!q.trim()) return;
    setShowHeroSuggestions(false); setShowNavSuggestions(false); setShowHeroFocusDropdown(false);
    navigate(buildSearchUrl({ q: q.trim() }));
  };

  /* Mobile: navigate immediately on submit of icon-search */
  const doMobileSearch = (q) => {
    if (!q || !q.trim()) { setMobileSearchOpen(false); return; }
    navigate(buildSearchUrl({ q: q.trim() }));
    setMobileSearchOpen(false);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        setShowHeroSuggestions(false); setShowHeroFocusDropdown(false); setShowNavSuggestions(false);
        setOpenBrowse(false); setOpenCategory(null); setSubcatDropPos(null);
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (openCategory !== null && subcatDropPos) {
        const dropdownEl = document.querySelector('[data-subcat-dropdown]');
        if (dropdownEl && !dropdownEl.contains(e.target)) { setOpenCategory(null); setSubcatDropPos(null); }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openCategory, subcatDropPos]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      if (heroSearchRef.current) {
        const rect = heroSearchRef.current.getBoundingClientRect();
        const visible = rect.bottom < 72;
        setShowNavSearch(visible);
        if (onShowNavSearchChange) onShowNavSearchChange(visible);
      }

    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [onShowNavSearchChange]);

  useEffect(() => {
    const handleClick = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpenBrowse(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const handler = () => { setShowHeroSuggestions(false); setShowNavSuggestions(false); setShowHeroFocusDropdown(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);


  /* Focus the mobile search input once it opens */
  useEffect(() => {
    if (mobileSearchOpen && mobileSearchInputRef.current) {
      mobileSearchInputRef.current.focus();
    }
  }, [mobileSearchOpen]);


  const handleNavBarEmptyClick = (e) => { if (e.target === e.currentTarget) setOpenBrowse(false); };

  const dropdownStyle = {
    position: "absolute", top: 48, left: 0, background: "#fff",
    boxShadow: "0 20px 60px rgba(0,0,0,0.12)", borderRadius: 14,
    border: "1px solid #E5E7EB", padding: 16, zIndex: 1100,
  };

  const navHeight = isMobile ? 60 : 72;
  const heroHeadlineFontSize = isMobile ? 32 : 64;
  const heroSubheadingFontSize = isMobile ? 14.5 : 18;
  const heroSubheadingMaxWidth = isMobile ? "100%" : 540;
  const heroSearchMaxWidth = isMobile ? "100%" : 750;
  const heroMinHeight = isMobile ? undefined : "calc(100vh - 72px)";

  return (
    <div
      className="w-full"
      style={{ fontFamily: F, paddingTop: navHeight, maxWidth: "100%", boxSizing: "border-box" }}
      onClick={() => { setShowHeroSuggestions(false); setShowNavSuggestions(false); setShowHeroFocusDropdown(false); }}
    >
      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideDownCentered {
          from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        #cat-scroll-bar::-webkit-scrollbar { display: none; }
        .hero-pills-row::-webkit-scrollbar { display: none; }

        .nav-text-link:hover {
          background: #F3F4F6 !important;
          color: #111827 !important;
        }
        .cta-btn:hover {
          transform: translateY(-1px) !important;
          background: ${C.greenDark} !important;
          box-shadow: 0 6px 20px rgba(30,146,84,0.32) !important;
        }
        .search-btn:hover {
          transform: translateY(-1px) !important;
          background: ${C.greenDark} !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.16) !important;
        }
        .category-pill:hover {
          background: rgba(255,255,255,0.20) !important;
          border-color: rgba(255,255,255,0.75) !important;
        }
        .hero-search-input:focus { outline: none; }
        .hero-search-wrap:focus-within .search-input-inner {
          box-shadow: 0 0 0 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.08) !important;
          transition: box-shadow 160ms ease-out !important;
        }
        html, body { overflow-x: hidden; max-width: 100%; }
      `}</style>

      {/* ═══════════════ NAVBAR ═══════════════ */}
      <header
        ref={navRef}
        onClick={handleNavBarEmptyClick}
        style={{
          position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000,
          background: navAppearsGreen
            ? `radial-gradient(ellipse at 30% 15%, ${C.greenDeep1} 0%, ${C.greenDeep2} 45%, ${C.greenDeep3} 100%)`
            : "#FFFFFF",
          backgroundAttachment: navAppearsGreen ? "fixed" : "scroll",
          backdropFilter: navAppearsGreen ? "none" : "blur(14px) saturate(1.6)",
          WebkitBackdropFilter: navAppearsGreen ? "none" : "blur(14px) saturate(1.6)",
          borderBottom: navAppearsGreen ? "none" : "1px solid #ECEEF1",
          boxShadow: navAppearsGreen ? "none" : "0 4px 24px rgba(15,17,23,0.07)",
          transition: "background 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease",
          fontFamily: F, boxSizing: "border-box",
        }}
      >
        <div
          onClick={handleNavBarEmptyClick}
          style={{
            maxWidth: 1400, margin: "0 auto", padding: "0 24px",
            height: navHeight, display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 16, overflow: "visible", boxSizing: "border-box",
          }}>

          {isMobile ? (
            <div style={{ flexShrink: 0, display: "flex", alignItems: "center", overflow: "visible", height: navHeight }}>
            <span style={{ display: "flex", alignItems: "center", lineHeight: 1 }}>
            <AppLogo height={26} theme={navAppearsGreen ? "onDark" : "onLight"} />
            </span>
            </div>
            ) : (
            <AppLogo height={44} theme={scrolled ? "onLight" : "onDark"} />
          )}

          {!isMobile && (
            <div
              style={{
                flex: 1, maxWidth: 480, margin: "0 32px", position: "relative",
                opacity: showNavSearch ? 1 : 0,
                transform: showNavSearch ? "translateY(0)" : "translateY(-6px)",
                pointerEvents: showNavSearch ? "auto" : "none",
                transition: "opacity 0.25s ease, transform 0.25s ease",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{
                display: "flex", alignItems: "center", background: "#fff",
                border: "1.5px solid #D1D5DB", borderRadius: 14, padding: "6px 6px 6px 16px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)", transition: "all 0.2s ease",
              }}>
                <input
                  value={navQuery || ""}
                  onChange={e => setNavQuery && setNavQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") doSearch(navQuery); }}
                  onFocus={() => navSuggestions.length > 0 && setShowNavSuggestions(true)}
                  placeholder="Find cleaners, fundis, plumbers…"
                  style={{ flex: 1, height: 32, border: "none", outline: "none", fontSize: 14, fontFamily: F, color: "#111827", background: "transparent" }}
                />
                <div style={{ width: 1, height: 20, background: "#D1D5DB", margin: "0 6px", flexShrink: 0 }} />
                <LocationDropdown selected={selectedLocation} onSelect={handleLocationChange} />
                <button className="search-btn" onClick={() => doSearch(navQuery)}
                  style={{ background: C.green, border: "none", borderRadius: 10, width: 36, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "all 0.2s ease" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                </button>
              </div>

              {showNavSuggestions && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 12px 32px rgba(0,0,0,0.10)", zIndex: 1200, overflow: "hidden" }}>
                  {navSuggestions.map((s, i) => (
                    <div key={i} onMouseDown={() => { setNavQuery && setNavQuery(s); doSearch(s); }}
                      style={{ padding: "10px 16px", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: "#374151", fontFamily: F, background: "#fff", transition: "all 0.2s ease" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                      onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.75" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!isMobile && (
            <div style={{
              display: "flex", alignItems: "center", gap: "clamp(2px, 0.5vw, 4px)",
              fontSize: "clamp(14px, 1.15vw, 15px)", color: "#1F2937",
              flexShrink: 0, whiteSpace: "nowrap", marginLeft: "auto", fontFamily: F,
            }}>
              <NavLinks
                authState={resolvedAuthState} openAuth={openAuth} navigate={navigate}
                openBrowse={openBrowse} setOpenBrowse={setOpenBrowse}
                navLinkStyle={getNavLinkStyle(scrolled)} dropdownStyle={dropdownStyle} exploreLinks={EXPLORE_LINKS}
                scrolled={scrolled} cartCount={cartCount} onCartClick={handleCartClick}
              />
            </div>
          )}

          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 2, marginLeft: "auto", flexShrink: 0 }}>
              {resolvedAuthState.isLoggedIn && (
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CartIcon count={cartCount} onClick={handleCartClick} size={32} />
                  <NotificationsDropdown />
                  <MessagesDropdown />
                </div>
              )}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  background: mobileMenuOpen ? "rgba(30,146,84,0.14)" : "transparent",
                  border: "none", cursor: "pointer", padding: 0,
                  borderRadius: 10,
                  flexShrink: 0, minHeight: 44, minWidth: 44,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.2s ease",
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round">
                  {mobileMenuOpen
                    ? <><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>
                    : <><path d="M3 12h18"/><path d="M3 6h18"/><path d="M3 18h18"/></>
                  }
                </svg>
              </button>
            </div>
          )}
        </div>
      </header>

      <MobileMenuDrawer
        open={isMobile && mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        navigate={navigate} openAuth={openAuth} authState={resolvedAuthState}
        buildSearchUrl={buildSearchUrl} exploreLinks={EXPLORE_LINKS}
        scrolled={scrolled} cartCount={cartCount} onCartClick={handleCartClick}
      />

      {/* ═══════════════ HERO — DESKTOP: video, unchanged spacing/categories/brands ═══════════════ */}
      {!isMobile && (
       <section ref={heroRef} className="relative w-full" style={{ minHeight: heroMinHeight || 560, maxWidth: "100%", boxSizing: "border-box", position: "relative", zIndex: 10, background: `radial-gradient(ellipse at 30% 15%, ${C.greenDeep1} 0%, ${C.greenDeep2} 45%, ${C.greenDeep3} 100%)`, backgroundAttachment: "fixed", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -60, right: -60, width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)", pointerEvents: "none" }}/>

          <div className="relative h-full max-w-[1200px] mx-auto px-6 flex flex-col" style={{ zIndex: 20, justifyContent: "space-between", paddingTop: "10vh", paddingBottom: "6vh" }}>
            <div>
              <h1 className="text-white max-w-[640px]" style={{ fontFamily: HERO_SERIF }}>
                <div style={{ fontSize: `clamp(30px, 6.5vw, ${heroHeadlineFontSize}px)`, lineHeight: 1.08, fontWeight: 400, letterSpacing: "-0.01em" }}>
                  Book a trusted <em style={{ fontStyle: "italic", color: "#7CDB9F" }}>Jirani</em> in minutes
                </div>
              </h1>
              <p style={{ marginTop: 14, marginBottom: 0, fontSize: `clamp(13.5px, 2vw, ${heroSubheadingFontSize}px)`, fontWeight: 400, color: "rgba(255,255,255,0.82)", fontFamily: F, lineHeight: 1.55, maxWidth: heroSubheadingMaxWidth, letterSpacing: "0.1px" }}>
                ID-verified cleaners, couriers, cooks and more, near you. Book in minutes and pay safely with <span style={{ display: "inline-flex", alignItems: "center", background: "rgba(255,255,255,0.92)", borderRadius: 5, padding: "3px 7px", verticalAlign: "-4px" }}><MPesaMark height={20} /></span>
              </p>
            </div>

            <div style={{ padding: "40px 0" }}>
              <div ref={heroSearchRef} className="w-full" style={{ position: "relative", maxWidth: heroSearchMaxWidth, width: "100%", boxSizing: "border-box" }} onClick={(e) => e.stopPropagation()}>
                <div className="search-input-inner" style={{
                  display: "flex", alignItems: "center", height: 46, borderRadius: 999, padding: "0 6px 0 18px",
                  background: "rgba(255,255,255,0.85)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.18)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.10)", transition: "box-shadow 160ms ease-out", minWidth: 0, boxSizing: "border-box",
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: "translateY(0.5px)", marginRight: 8, display: "block" }}>
                    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
                  </svg>
                  <input
                    className="hero-search-input"
                    value={heroQuery}
                    onChange={(e) => setHeroQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") doSearch(heroQuery); }}
                    onFocus={() => {
                      if (heroQuery.trim().length >= 2 && heroSuggestions.length > 0) setShowHeroSuggestions(true);
                      else if (heroQuery.trim().length < 2) setShowHeroFocusDropdown(true);
                    }}
                    placeholder="Find cleaners, fundis, plumbers…"
                    style={{ flex: 1, minWidth: 0, height: "100%", fontSize: 15, lineHeight: 1, border: "none", outline: "none", background: "transparent", fontFamily: F, color: "#111827", letterSpacing: "0.2px" }}
                  />
                  <div style={{ width: 1, height: 20, background: "#D1D5DB", margin: "0 8px", flexShrink: 0 }} />
                  <LocationDropdown selected={selectedLocation} onSelect={handleLocationChange} />
                  <button className="search-btn" onClick={() => doSearch(heroQuery)} style={{
                    background: C.green, borderRadius: 999, height: 34, width: 34, flexShrink: 0, border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 4, boxShadow: "0 2px 10px rgba(0,0,0,0.12)", transition: "all 0.2s ease",
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                  </button>
                </div>

                {showHeroFocusDropdown && !showHeroSuggestions && (
                  <div style={{ position: "fixed", top: heroSearchRef.current ? heroSearchRef.current.getBoundingClientRect().bottom + 8 : "auto", left: heroSearchRef.current ? heroSearchRef.current.getBoundingClientRect().left : "auto", width: heroSearchRef.current ? heroSearchRef.current.getBoundingClientRect().width : "auto", zIndex: 9999 }}>
                    <SearchFocusDropdown onSelect={(label) => { setHeroQuery(label); doSearch(label); }} />
                  </div>
                )}

                {showHeroSuggestions && (
                  <div style={{
                    position: "fixed", top: heroSearchRef.current ? heroSearchRef.current.getBoundingClientRect().bottom + 8 : "auto",
                    left: heroSearchRef.current ? heroSearchRef.current.getBoundingClientRect().left : "auto",
                    width: heroSearchRef.current ? heroSearchRef.current.getBoundingClientRect().width : "auto",
                    background: "#fff", borderRadius: 14, boxShadow: "0 12px 40px rgba(0,0,0,0.13)", zIndex: 9999, overflow: "hidden", animation: "fadeSlideDown 0.15s ease both",
                  }}>
                    {heroSuggestions.map((s, i) => {
                      const idx = s.toLowerCase().indexOf(heroQuery.toLowerCase());
                      const before = s.slice(0, idx); const match = s.slice(idx, idx + heroQuery.length); const after = s.slice(idx + heroQuery.length);
                      return (
                        <div key={i} onMouseDown={() => { setHeroQuery(s); doSearch(s); }}
                          style={{ padding: "11px 24px", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, color: "#374151", fontFamily: F, transition: "all 0.2s ease" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.75" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                          <span><span style={{ fontWeight: 700, color: "#111827" }}>{before}</span><span style={{ color: "#374151" }}>{match}</span><span style={{ fontWeight: 700, color: "#111827" }}>{after}</span></span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* CATEGORY PILLS — desktop, unchanged */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 20 }}>
                <button onClick={() => scrollPills(-1)} style={{
                  flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.14)",
                  backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.38)",
                  cursor: pillsCanScrollLeft ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "opacity 0.25s ease, background 0.2s ease, transform 0.2s ease",
                  opacity: pillsCanScrollLeft ? 1 : 0.22, pointerEvents: pillsCanScrollLeft ? "auto" : "none", boxShadow: "0 1px 8px rgba(0,0,0,0.18)",
                }}
                  onMouseEnter={e => { if (pillsCanScrollLeft) { e.currentTarget.style.background = "rgba(255,255,255,0.26)"; e.currentTarget.style.transform = "scale(1.1)"; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.14)"; e.currentTarget.style.transform = "scale(1)"; }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                </button>

                <div ref={(el) => { categoryRef.current = el; pillsScrollRef.current = el; }} className="hero-pills-row" onScroll={updatePillsScroll} style={{
                  display: "flex", gap: 8, flex: 1, minWidth: 0, flexWrap: "nowrap", overflowX: "auto", overflowY: "visible",
                  scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch", paddingBottom: 4, boxSizing: "border-box",
                  maskImage: pillsCanScrollLeft && pillsCanScrollRight ? "linear-gradient(to right, transparent 0px, #000 36px, #000 calc(100% - 36px), transparent 100%)" : pillsCanScrollLeft ? "linear-gradient(to right, transparent 0px, #000 36px)" : pillsCanScrollRight ? "linear-gradient(to left, transparent 0px, #000 36px)" : "none",
                  WebkitMaskImage: pillsCanScrollLeft && pillsCanScrollRight ? "linear-gradient(to right, transparent 0px, #000 36px, #000 calc(100% - 36px), transparent 100%)" : pillsCanScrollLeft ? "linear-gradient(to right, transparent 0px, #000 36px)" : pillsCanScrollRight ? "linear-gradient(to left, transparent 0px, #000 36px)" : "none",
                }}>
                  {categories.map((cat, index) => (
                    <div key={index} className="relative">
                      <button
                        data-pill-index={index}
                        className="category-pill text-white flex items-center gap-1.5 text-[12px]"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (openCategory === index) { setOpenCategory(null); setSubcatDropPos(null); }
                          else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setSubcatDropPos({ index, top: rect.bottom + 4, left: rect.left });
                            setOpenCategory(index);
                          }
                        }}
                        style={{
                          fontFamily: F, fontWeight: 550, background: "rgba(255,255,255,0.14)", backdropFilter: "blur(4px)",
                          border: "1px solid rgba(255,255,255,0.52)", borderRadius: 10, padding: "6px 14px", letterSpacing: "0.15px",
                          transition: "all 0.2s ease", whiteSpace: "nowrap", color: "white",
                        }}
                      >
                        {cat.name}
                        <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2.5"><path d="M5 8l5 5 5-5"/></svg>
                      </button>
                    </div>
                  ))}
                </div>

                <button onClick={() => scrollPills(1)} style={{
                  flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.14)",
                  backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.38)",
                  cursor: pillsCanScrollRight ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "opacity 0.25s ease, background 0.2s ease, transform 0.2s ease",
                  opacity: pillsCanScrollRight ? 1 : 0.22, pointerEvents: pillsCanScrollRight ? "auto" : "none", boxShadow: "0 1px 8px rgba(0,0,0,0.18)",
                }}
                  onMouseEnter={e => { if (pillsCanScrollRight) { e.currentTarget.style.background = "rgba(255,255,255,0.26)"; e.currentTarget.style.transform = "scale(1.1)"; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.14)"; e.currentTarget.style.transform = "scale(1)"; }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
            </div>

            {/* Subcategory pill dropdown */}
            {openCategory !== null && subcatDropPos && createPortal(
              <div data-subcat-dropdown style={{
                position: "fixed", top: subcatDropPos.top + "px", left: subcatDropPos.left + "px", width: 220,
                background: "#fff", borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.14)", border: "1px solid #E5E7EB",
                zIndex: 99998, maxHeight: 320, overflowY: "auto", animation: "fadeSlideDown 0.15s ease both",
              }} onClick={e => e.stopPropagation()}>
                {categories[subcatDropPos.index]?.columns.map((col) =>
                  col.links.map((link, li) => (
                    <div key={`${col.heading}-${li}`} style={{ padding: "9px 16px", fontSize: 13, color: "#374151", cursor: "pointer", fontFamily: F, transition: "background 0.15s ease" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      onClick={() => { setOpenCategory(null); setSubcatDropPos(null); navigate(buildSearchUrl({ q: link })); }}
                    >{link}</div>
                  ))
                )}
              </div>, document.body
            )}

            <div>
              <ProShowcaseStrip isMobile={false} navigate={navigate} />
            </div>
          </div>


        </section>
      )}

      {/* ═══════════════ HERO — MOBILE: no video, green gradient ═══════════════ */}
      {isMobile && (
        <section
          className="relative w-full"
          style={{
            background: `radial-gradient(ellipse at 30% 15%, ${C.greenDeep1} 0%, ${C.greenDeep2} 45%, ${C.greenDeep3} 100%)`,
            backgroundAttachment: "fixed",
            padding: "32px 20px 36px",
            boxSizing: "border-box",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Subtle texture: faint radial highlight, no imagery needed */}
          <div style={{
            position: "absolute", top: -60, right: -60, width: 220, height: 220, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)", pointerEvents: "none",
          }}/>

          <div style={{ position: "relative", zIndex: 2 }}>
            {/* Headline */}
            <h1 style={{ fontFamily: HERO_SERIF, color: "#fff", margin: 0 }}>
              <div style={{ fontSize: `clamp(28px, 8vw, ${heroHeadlineFontSize}px)`, lineHeight: 1.1, fontWeight: 400, letterSpacing: "-0.01em" }}>
                Book a trusted <em style={{ fontStyle: "italic", color: "#7CDB9F" }}>Jirani</em> in minutes
              </div>
            </h1>

            <p style={{
              marginTop: 12, marginBottom: 0, fontSize: 14.5, fontWeight: 400,
              color: "rgba(255,255,255,0.78)", fontFamily: F, lineHeight: 1.6, letterSpacing: "0.1px",
            }}>
              ID-verified cleaners, couriers, cooks and more, near you. Book a trusted pro and pay safely with <span style={{ display: "inline-flex", alignItems: "center", background: "rgba(255,255,255,0.92)", borderRadius: 5, padding: "2px 5px", verticalAlign: "-3px" }}><MPesaMark height={13} /></span>
            </p>

            {/* ── Search bar: input + location dropdown + green search button only, no icon/placeholder clutter ── */}
            <div ref={heroSearchRef} style={{ marginTop: 26, position: "relative" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                background: "rgba(255,255,255,0.96)", borderRadius: 999,
                padding: "6px 6px 6px 18px", boxShadow: "0 8px 24px rgba(0,0,0,0.22)",
              }}>
                <input
                  value={heroQuery}
                  onChange={(e) => setHeroQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") doMobileSearch(heroQuery); }}
                  onFocus={() => { if (heroQuery.trim().length >= 2 && heroSuggestions.length > 0) setShowHeroSuggestions(true); }}
                  placeholder="Plumbers, cleaners…"
                  style={{ flex: 1, minWidth: 0, height: 38, border: "none", outline: "none", fontSize: 14.5, fontFamily: F, color: "#111827", background: "transparent" }}
                />
                <div style={{ width: 1, height: 20, background: "#D1D5DB", flexShrink: 0 }} />
                <LocationDropdown selected={selectedLocation} onSelect={handleLocationChange} />
                <button
                  onClick={() => doMobileSearch(heroQuery)}
                  style={{
                    background: C.green, borderRadius: 999, height: 38, width: 38, flexShrink: 0,
                    border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
                  }}
                  aria-label="Search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                </button>
              </div>

              {/* Suggestions */}
              {showHeroSuggestions && (
                <div style={{
                  marginTop: 8, background: "#fff", borderRadius: 14,
                  boxShadow: "0 12px 32px rgba(0,0,0,0.2)", overflow: "hidden",
                  animation: "fadeSlideDown 0.15s ease both",
                }}>
                  {heroSuggestions.slice(0, 5).map((s, i) => (
                    <div key={i} onMouseDown={() => { setHeroQuery(s); doMobileSearch(s); }}
                      style={{ padding: "11px 18px", fontSize: 14, color: "#374151", fontFamily: F, display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.75" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pro showcase strip — mobile */}
            <div style={{ marginTop: 28 }}>
              <ProShowcaseStrip isMobile={true} navigate={navigate} />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}